import React, { useEffect, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import Grid from "../components/Grid"; // Import the Grid component
import { FaEdit } from "react-icons/fa";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";
import { FaDownload } from "react-icons/fa"; 
import ExcelJS from "exceljs";

// Use the environment variable for the API base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Organizations({ onAddOrganizationClick }) {
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sort] = useState({ field: "organization_name", direction: "asc" });
  const [selectedSearchField, setSelectedSearchField] = useState("organization_name"); // New state for search field


  useEffect(() => {
    // Fetch organizations from the API when the component mounts
    const fetchOrganizations = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/organizations`
        );
        // Map the organization to include an id field
        const organizationsWithId = response.data.organizations.map(
          (organization) => ({
            ...organization,
            id: organization.organization_code, // Assign _id to id
          })
        );
        setOrganizations(organizationsWithId); // Set organizations with id field
      } catch (error) {
        console.error("Error fetching organizations:", error);
        toast.error("Error fetching organizations");
      }
    };

    fetchOrganizations();
  }, []);

  const handleOrganizationClick = (organization) => {
    setSelectedOrganization({
      ...organization,
      organization_name: organization.organization_name || "",
      organization_BRN: organization.organization_BRN || "",
      status: organization.status || "Active",
      organization_prefix: organization.organization_prefix || "",
    });
  };

  const handleClosePopup = () => {
    setSelectedOrganization(null);
  };

 // Filter and sort the production batches
 const filteredOrganizations = organizations
 .filter((organization) => {
   switch (selectedSearchField) {
     case "organization_code":
       return (
         organization.organization_code &&
         organization.organization_code.toLowerCase().includes(searchTerm.toLowerCase())
       );
       case "organization_prefix":
       return (
         organization.organization_prefix &&
         organization.organization_prefix.toLowerCase().includes(searchTerm.toLowerCase())
       );
     case "organization_name":
       return (
         organization.organization_name &&
         organization.organization_name.toLowerCase().includes(searchTerm.toLowerCase())
       );
    case "organization_BRN":
      return (
        organization.organization_BRN &&
        organization.organization_BRN.toLowerCase().includes(searchTerm.toLowerCase())
      );
      case "owner_name":
        return (
          organization.owner_name &&
          organization.owner_name.toLowerCase().includes(searchTerm.toLowerCase())
        );
      case "status":  
        return (
          organization.status &&
          organization.status.toLowerCase().includes(searchTerm.toLowerCase())
        );
     default:
       return false;
   }
 })
 .sort((a, b) => {
   if (a[sort.field] < b[sort.field])
     return sort.direction === "asc" ? -1 : 1;
   if (a[sort.field] > b[sort.field])
     return sort.direction === "asc" ? 1 : -1;
   return 0;
 });

 const handleDownloadExcel = async () => {
  const dataToExport =
    searchTerm.trim().length > 0 ? filteredOrganizations : organizations;

    if (dataToExport.length === 0) {
      toast.warn("No organization data to export.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Organizations");

    const headerRow = [
      "Organization Code",
      "Prefix",
      "Organization Name",
      "Organization BRN",
      "Owner Name",
      "Status"
    ];
    worksheet.addRow(headerRow);

    const header = worksheet.getRow(1);
    header.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD966" }, // Light yellow
      };
      cell.font = { bold: true };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    dataToExport.forEach((org) => {
      worksheet.addRow([
        org.organization_code || "N/A",
        org.organization_prefix || "N/A",
        org.organization_name || "N/A",
        org.organization_BRN || "N/A",
        org.owner_name || "N/A",
        org.status || "N/A"
      ]);
    });

    worksheet.columns.forEach((column) => {
      column.width = column.header
        ? column.header.toString().length < 20
          ? 20
          : column.header.toString().length
        : 20;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "organizations.xlsx");
  };

  const columns = [
    { field: "organization_code", headerName: "ID", flex: 1 }, // Use id here
    { field: "organization_prefix", headerName: "Prefix", flex: 1 },
    { field: "organization_name", headerName: "Organization Name", flex: 1 },
    { field: "organization_BRN", headerName: "Organization BRN", flex: 1 },
    { field: "owner_name", headerName: "Organization Owner Name", flex: 1 },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => {
        const status = params.row.status;
        const statusClass =
          status === "Active"
            ? "bg-green-500 text-white"
            : "bg-red-500 text-white";

        return (
          <span className={`px-2 py-1 rounded-sm ${statusClass}`}>
            {status}
          </span>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      renderCell: (params) => (
        <div className="flex justify-left items-left w-10 h-10">
          <button
            onClick={() => handleOrganizationClick(params.row)}
            className="bg-blue-500 text-white px-2 py-1 flex items-center rounded-lg"
          >
            <FaEdit className="text-white mr-2" />
            Edit
          </button>
        </div>
      ),
    },
  ];

  const handleUpdateOrganization = async () => {
    try {
      await axios.put(
        `${API_BASE_URL}/organizations/update/${selectedOrganization.organization_code}`,
        {
          ...selectedOrganization,
        }
      );
      toast.success("Customer Updated Succesfully");
      setSelectedOrganization(null);
      // Refresh the customer list
      const response = await axios.get(
        `${API_BASE_URL}/organizations`
      );
      setOrganizations(response.data.organizations);
    } catch (error) {
      console.error("Error updating organization:", error);
      toast.error("Error Updating Organization");
    }
  };

  return (
    <div className="flex h-screen">
      {/* Main Content */}
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-semibold">Organizations</h1>
          <button
            onClick={onAddOrganizationClick}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg"
          >
            Add Organization
          </button>
        </div>

        {/* Search and Filter */}
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex justify-between mb-4 items-center flex-wrap gap-4">
             {/* Left Section: Search filters */}
             <div className="flex items-center flex-wrap gap-4">
               <select
                 className="px-4 py-2 border border-gray-300 rounded-lg"
                 value={selectedSearchField}
                 onChange={(e) => setSelectedSearchField(e.target.value)}
               >
                <option value="organization_name">Organization Name</option>
                <option value="organization_prefix">Organization Prefix</option>
                <option value="organization_code">Code</option>
                <option value="organization_BRN">Organization BRN</option>
                <option value="owner_name">Owner Name</option>
                <option value="status">Status</option>
               </select>
 
               <div className="relative">
                 <input
                   type="text"
                   placeholder="Search Batch here"
                   value={searchTerm}
                   onChange={(e) => setSearchTerm(e.target.value)}
                   className="px-4 py-2 pl-10 border border-gray-300 rounded-lg w-full max-w-xs"
                 />
                 <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                   <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
                 </div>
               </div>
             </div>
 
             {/* Right Section: Download Excel */}
             <div>
               <button
                 onClick={handleDownloadExcel}
                 className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
               >
                 <FaDownload className="mr-2" />
                 Download Excel
               </button>
             </div>
           </div>         

          {/* Product Grid */}
          <Grid rows={filteredOrganizations} columns={columns} getRowId={(row) => row.organization_name}/>
        </div>

        {/* Popup for Organization Details */}
        {selectedOrganization && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
            <div className="bg-white p-8 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 relative">
              <button
                onClick={handleClosePopup}
                className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded-sm hover:bg-red-700"
              >
                &times;
              </button>
              <div className="flex mb-4">
                <div className="w-1/2 pl-4">
                  <h2 className="text-xl font-semibold mb-4">
                    {selectedOrganization.organization_name}
                  </h2>
                  <h3 className="text-xl font-semibold mb-4">
                    {selectedOrganization.organization_prefix}
                  </h3>
                  {/*organization BRN */}
                  <div className="mb-4">
                    <label className="block text-gray-700">
                      Organization BRN
                    </label>
                    <input
                      type="text"
                      value={selectedOrganization.organization_BRN}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      onChange={(e) =>
                        setSelectedOrganization({
                          ...selectedOrganization,
                          organization_BRN: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-700">Owner Name</label>
                    <input
                      type="text"
                      value={selectedOrganization.owner_name}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      onChange={(e) =>
                        setSelectedOrganization({
                          ...selectedOrganization,
                          owner_name: e.target.value,
                        })
                      }
                    />
                  </div>
                  <button
                    onClick={handleUpdateOrganization}
                    className="bg-blue-500 text-white px-4 py-2 rounded-lg"
                  >
                    Update
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Organizations;
